import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import './Navbar.css';

/* ── icons ──────────────────────────────────────────────────────────────── */
const SunIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="4" /><line x1="12" y1="2" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="22" /><line x1="4.22" y1="4.22" x2="7.05" y2="7.05" /><line x1="16.95" y1="16.95" x2="19.78" y2="19.78" /><line x1="2" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="22" y2="12" /><line x1="4.22" y1="19.78" x2="7.05" y2="16.95" /><line x1="16.95" y1="7.05" x2="19.78" y2="4.22" /></svg>;
const MoonIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>;
const MenuIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>;
const CloseIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>;
const BellIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>;

export default function Navbar({ walletBalance }) {
  const { user, theme, lang, unread, toggleTheme, toggleLang, logout, t } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const isActive = path => location.pathname === path || location.pathname.startsWith(path + '/');
  const close = () => setOpen(false);
  const handleLogout = () => { logout(); navigate('/'); close(); };

  /* role-based nav links */
  const mainLinks = [
    { to: '/jobs', label: t('nav_find_job'), always: true },
    { to: '/find-hire', label: t('nav_find_hire'), always: true },
    { to: '/post-job', label: t('nav_post_job'), roles: ['POSTER', 'SUPERADMIN'] },
    { to: '/post-seek-ad', label: t('nav_post_seek'), roles: ['SEEKER'] },


  ].filter(l => l.always || (user && l.roles?.includes(user.role)));

  const roleLink = !user ? null
    : user.role === 'SUPERADMIN' ? { to: '/admin', label: t('admin_panel') }
      : user.role === 'MODERATOR' ? { to: '/moderator', label: t('moderator_panel') }
        : null;

  /* ── theme/lang pills ──────────────────────────────────────────────────── */
  const ThemeToggle = () => (
    <div className="toggle-pill">
      <button className={`toggle-btn${theme === 'light' ? ' on' : ''}`} onClick={toggleTheme} title="Light"><SunIcon /></button>
      <button className={`toggle-btn${theme === 'dark' ? ' on' : ''}`} onClick={toggleTheme} title="Dark"><MoonIcon /></button>
    </div>
  );
  const LangToggle = () => (
    <div className="toggle-pill">
      <button className={`toggle-btn${lang === 'en' ? ' on' : ''}`} onClick={toggleLang}>EN</button>
      <button className={`toggle-btn${lang === 'bn' ? ' on' : ''}`} onClick={toggleLang}>বাং</button>
    </div>
  );

  return (
    <>
      <nav className="navbar">
        <div className="navbar-inner">
          {/* Logo */}
          <Link to="/" className="navbar-logo" onClick={close}>
            <span className="logo-k">Kajer</span><span className="logo-b">Bazar</span>
          </Link>

          {/* Desktop links */}
          <div className="navbar-links hide-mobile">
            {mainLinks.map(l => (
              <Link key={l.to} to={l.to} className={`nav-link${isActive(l.to) ? ' active' : ''}`}>{l.label}</Link>
            ))}
            {roleLink && <Link to={roleLink.to} className={`nav-link${isActive(roleLink.to) ? ' active' : ''}`}>{roleLink.label}</Link>}
          </div>

          {/* Desktop actions */}
          <div className="navbar-actions hide-mobile">
            <ThemeToggle />
            <LangToggle />
            {user ? (
              <>
                {/* User Info */}
                <Link to="/profile" className="nav-user-display">
                  <div className="nav-user-avatar">{user.fullName?.[0]?.toUpperCase()}</div>
                  <span>{user.fullName}</span>
                </Link>



                {/* Notification bell */}
                <Link to="/notifications" className="notif-btn" title="Notifications">
                  <BellIcon />
                  {unread > 0 && <span className="notif-badge">{unread > 9 ? '9+' : unread}</span>}
                </Link>
                {/* Wallet */}
                <Link to="/wallet" className="token-badge nav-token">
                  🪙 {walletBalance ?? '—'}
                </Link>
                {/* Dashboard */}
                <Link to="/dashboard" className={`btn btn-outline btn-sm${isActive('/dashboard') ? ' active-btn' : ''}`}>
                  {t('nav_dashboard')}
                </Link>
                <button className="btn btn-sm btn-outline" onClick={handleLogout}>{t('nav_logout')}</button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn btn-outline btn-sm">{t('nav_login')}</Link>
                <Link to="/register" className="btn btn-primary btn-sm">{t('nav_register')}</Link>
              </>
            )}
          </div>

          {/* Mobile actions */}
          <div className="navbar-actions hide-desktop" style={{ marginLeft: 'auto', gap: '8px' }}>
            {user && (
              <Link to="/wallet" className="token-badge nav-token" style={{ padding: '4px 8px', fontSize: '12px' }}>
                🪙 {walletBalance ?? '—'}
              </Link>
            )}
            <ThemeToggle />
            <button className="hamburger" onClick={() => setOpen(o => !o)} aria-label="Menu">
              {open ? <CloseIcon /> : <MenuIcon />}
            </button>
          </div>
        </div>
      </nav>

      {/* ── Mobile drawer ──────────────────────────────────────────────────── */}
      {open && <div className="drawer-overlay" onClick={close} />}
      <aside className={`drawer${open ? ' open' : ''}`}>
        <div className="drawer-header">
          <Link to="/" className="navbar-logo" onClick={close}>
            <span className="logo-k">Kajer</span><span className="logo-b">Bazar</span>
          </Link>
          <button className="hamburger" onClick={close}><CloseIcon /></button>
        </div>

        {/* Controls inside drawer (Top) */}
        <div className="drawer-controls" style={{ padding: '16px 20px 8px' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{ flex: 1 }}>
              <div className="drawer-ctrl-label">Theme</div>
              <ThemeToggle />
            </div>
            <div style={{ flex: 1 }}>
              <div className="drawer-ctrl-label">Language</div>
              <LangToggle />
            </div>
          </div>
        </div>

        <div className="drawer-divider" style={{ margin: '8px 0' }} />

        <nav className="drawer-nav">

          {mainLinks.map(l => (
            <Link key={l.to} to={l.to} className={`drawer-link${isActive(l.to) ? ' active' : ''}`} onClick={close}>{l.label}</Link>
          ))}
          {roleLink && <Link to={roleLink.to} className={`drawer-link${isActive(roleLink.to) ? ' active' : ''}`} onClick={close}>{roleLink.label}</Link>}
          {user && <>
            <Link to="/dashboard" className={`drawer-link${isActive('/dashboard') ? ' active' : ''}`} onClick={close}>{t('nav_dashboard')}</Link>
            <Link to="/wallet" className={`drawer-link${isActive('/wallet') ? ' active' : ''}`} onClick={close}>{t('nav_wallet')}</Link>
            <Link to="/notifications" className={`drawer-link${isActive('/notifications') ? ' active' : ''}`} onClick={close}>
              {t('notifications')}{unread > 0 && <span className="drawer-badge">{unread}</span>}
            </Link>
          </>}
        </nav>

        <div className="drawer-divider" />


        <div className="drawer-divider" />

        {/* Auth footer */}
        <div className="drawer-auth">
          {user ? (
            <>
              <Link to="/profile" className="drawer-user" onClick={close} style={{ textDecoration: 'none', color: 'inherit' }}>
                <div className="drawer-avatar">{user.fullName?.[0]?.toUpperCase()}</div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text)' }}>{user.fullName}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text3)' }}>{user.role} · 🪙 {walletBalance ?? '—'}</div>
                </div>
              </Link>

              <button className="btn btn-outline btn-full" style={{ marginTop: '10px' }} onClick={handleLogout}>{t('nav_logout')}</button>
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Link to="/login" className="btn btn-outline btn-full" onClick={close}>{t('nav_login')}</Link>
              <Link to="/register" className="btn btn-primary btn-full" onClick={close}>{t('nav_register')}</Link>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
